import { useId, useState, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  label: string;
  value?: string;
  error?: string;
  onChange?: (value: string) => void;
  highlight?: boolean;
}

export function TextAreaField({
  label,
  value = "",
  error,
  onChange,
  className,
  highlight,
  // `readOnly` est déjà un attribut natif du textarea et passerait par le
  // spread — on le destructure pour piloter AUSSI l'apparence : même boîte,
  // même place, mais ni bordure au survol ni cadre au focus. Cf.
  // `FormField.readOnly`, contrat identique.
  readOnly = false,
  ...props
}: TextAreaFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isFilled = value.length > 0;
  const generatedId = useId();
  const inputId = props.id ?? generatedId;

  const borderClasses = readOnly
    ? "border border-solid border-transparent cursor-default"
    : error
    ? "border border-solid border-danger"
    : isFocused
      ? "border border-solid border-border-focus"
      : isFilled && isHovered
        ? "border border-dashed border-border-hover"
        : isFilled
          ? "border border-solid border-transparent"
          : isHovered
            ? "border border-dashed border-border-hover"
            : "border border-dashed border-border";

  const showCounter = typeof props.maxLength === "number";

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="flex items-baseline justify-between px-3 mb-1">
        <label htmlFor={inputId} className={cn("text-xs font-medium tracking-wider uppercase", highlight ? "text-danger" : "text-text-muted")}>
          {label}
        </label>
        {showCounter && (
          <span className={cn(
            "text-[10px] font-medium",
            value.length >= (props.maxLength ?? 0) ? "text-danger" : "text-text-muted",
          )}>
            {value.length}/{props.maxLength}
          </span>
        )}
      </div>
      <textarea
        className={cn(
          "px-3 py-2 bg-surface-2 rounded-md text-sm text-text-primary",
          "placeholder:text-text-muted outline-none shadow-none",
          "transition-colors duration-150 min-h-[120px] resize-y",
          borderClasses
        )}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => !readOnly && setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onMouseEnter={() => !readOnly && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        placeholder={readOnly ? "—" : "..."}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        {...props}
        id={inputId}
      />
      {error && (
        <span className="text-xs text-danger mt-1 px-3">{error}</span>
      )}
    </div>
  );
}
